import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Paper, Chip, Dialog, DialogTitle, DialogContent, DialogActions,
  Button, List, ListItem, ListItemText, ListItemIcon, Stack, Divider, CircularProgress, Alert,
} from '@mui/material';
import { GridLegacy as Grid } from '@mui/material';
import {
  EventAvailable, WarningAmber, Celebration, FlightTakeoff, EmojiEvents,
  History, Assignment, CheckCircle, Cancel, ArrowForward, PlayCircleFilled,
} from '@mui/icons-material';
import { format, startOfMonth, parseISO, isBefore, addDays, subDays } from 'date-fns';
import {
  leaveBalanceService, attendanceService, holidayService,
  leaveRequestService, timesheetService,
} from '../services/api';
import { useAuth } from '../contexts/AuthContext';

interface LeaveBalance {
  sickLeaveBalance: number;
  earnedLeaveBalance: number;
  totalBalance: number;
  pendingSickLeave?: number;
  pendingEarnedLeave?: number;
}

interface AttendanceRecord {
  id: number;
  date: string;
  status: string;
}

interface Holiday {
  id: number;
  name: string;
  date: string;
  description?: string;
  isActive: boolean;
}

interface LeaveRequest {
  id: number;
  startDate: string;
  endDate: string;
  leaveType: string;
  reason: string;
  status: string;
  requestedAt: string;
  isUnpaid?: boolean;
  dayType?: string;
  paidDays?: number;
}

interface Timesheet {
  id: number;
  date: string;
  hours: number;
  status: string;
}

interface MissedDay {
  date: Date;
  dateStr: string;
  isToday: boolean;
}

const today = new Date();
const todayStr = format(today, 'yyyy-MM-dd');

const dateKey = (iso: string) => iso.split('T')[0];

const leaveStatusColor = (status: string) =>
  status === 'Approved' ? 'success' : status === 'Rejected' ? 'error' : status === 'Cancelled' ? 'default' : 'warning';

/* ── Reusable stat card ───────────────────────────────────── */

const DashboardCard: React.FC<{
  icon: React.ReactNode;
  iconColor: string;
  title: string;
  onClick?: () => void;
  children: React.ReactNode;
}> = ({ icon, iconColor, title, onClick, children }) => (
  <Paper
    variant="outlined"
    onClick={onClick}
    sx={{
      p: 2.25, height: '100%', display: 'flex', flexDirection: 'column', gap: 1.25,
      cursor: onClick ? 'pointer' : 'default',
      transition: 'border-color .15s ease, box-shadow .15s ease',
      '&:hover': onClick ? { borderColor: 'primary.light', boxShadow: 2 } : undefined,
    }}
  >
    <Stack direction="row" spacing={1.5} alignItems="center">
      <Box sx={{
        width: 36, height: 36, borderRadius: '10px', bgcolor: iconColor,
        display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', flexShrink: 0,
        '& svg': { fontSize: 19 },
      }}>
        {icon}
      </Box>
      <Typography variant="subtitle2" color="text.secondary" fontWeight={700} sx={{ fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.03em' }}>
        {title}
      </Typography>
      {onClick && <ArrowForward sx={{ fontSize: 15, color: 'text.disabled', ml: 'auto' }} />}
    </Stack>
    <Box sx={{ flexGrow: 1 }}>{children}</Box>
  </Paper>
);

const MyDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { user, can } = useAuth();

  const [loading, setLoading] = useState(true);
  const [balance, setBalance] = useState<LeaveBalance | null>(null);
  const [attendance, setAttendance] = useState<AttendanceRecord[]>([]);
  const [holidays, setHolidays] = useState<Holiday[]>([]);
  const [leaveRequests, setLeaveRequests] = useState<LeaveRequest[]>([]);
  const [timesheets, setTimesheets] = useState<Timesheet[]>([]);

  const [missedDialogOpen, setMissedDialogOpen] = useState(false);
  const [leavesDialogOpen, setLeavesDialogOpen] = useState(false);
  const [timesheetDialogOpen, setTimesheetDialogOpen] = useState(false);
  const [marking, setMarking] = useState(false);
  const [markError, setMarkError] = useState('');

  const loadAll = async () => {
    setLoading(true);
    const monthStartStr = format(startOfMonth(today), 'yyyy-MM-dd');
    const [balanceR, attendanceR, holidaysR, leavesR, timesheetsR] = await Promise.allSettled([
      leaveBalanceService.getMyBalance(),
      attendanceService.getMyAttendance(monthStartStr, todayStr),
      holidayService.getHolidays(),
      leaveRequestService.getMyLeaveRequests(),
      timesheetService.getMyTimesheets(),
    ]);
    if (balanceR.status === 'fulfilled') setBalance(balanceR.value);
    if (attendanceR.status === 'fulfilled') setAttendance(attendanceR.value || []);
    if (holidaysR.status === 'fulfilled') setHolidays(holidaysR.value || []);
    if (leavesR.status === 'fulfilled') setLeaveRequests(leavesR.value || []);
    if (timesheetsR.status === 'fulfilled') setTimesheets(timesheetsR.value || []);
    setLoading(false);
  };

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ── Derived data ───────────────────────────────────────── */

  const missedDays = useMemo<MissedDay[]>(() => {
    const attendedDates = new Set(attendance.map(a => dateKey(a.date)));
    const holidayDates = new Set(holidays.filter(h => h.isActive).map(h => dateKey(h.date)));
    const leaveRanges = leaveRequests
      .filter(l => l.status === 'Approved' || l.status === 'Pending')
      .map(l => ({ start: parseISO(dateKey(l.startDate)), end: parseISO(dateKey(l.endDate)) }));
    const isOnLeave = (d: Date) => leaveRanges.some(r => d >= r.start && d <= r.end);

    const days: MissedDay[] = [];
    let cursor = startOfMonth(today);
    while (cursor <= today) {
      const dateStr = format(cursor, 'yyyy-MM-dd');
      const isSunday = cursor.getDay() === 0;
      if (!isSunday && !holidayDates.has(dateStr) && !attendedDates.has(dateStr) && !isOnLeave(cursor)) {
        days.push({ date: new Date(cursor), dateStr, isToday: dateStr === todayStr });
      }
      cursor = addDays(cursor, 1);
    }
    return days;
  }, [attendance, holidays, leaveRequests]);

  const pastMissedCount = missedDays.filter(d => !d.isToday).length;
  const todayPending = missedDays.some(d => d.isToday);

  const upcomingHolidays = useMemo(() => {
    return holidays
      .filter(h => h.isActive && !isBefore(parseISO(dateKey(h.date)), today))
      .sort((a, b) => parseISO(a.date).getTime() - parseISO(b.date).getTime());
  }, [holidays]);
  const nextHoliday = upcomingHolidays[0];

  const upcomingLeaves = useMemo(() => {
    return leaveRequests
      .filter(l => (l.status === 'Approved' || l.status === 'Pending') && !isBefore(parseISO(dateKey(l.startDate)), today))
      .sort((a, b) => parseISO(a.startDate).getTime() - parseISO(b.startDate).getTime());
  }, [leaveRequests]);
  const nextLeave = upcomingLeaves[0];

  const last5Leaves = useMemo(() => {
    return [...leaveRequests]
      .sort((a, b) => parseISO(b.requestedAt).getTime() - parseISO(a.requestedAt).getTime())
      .slice(0, 5);
  }, [leaveRequests]);

  const timesheetDates = useMemo(() => new Set(timesheets.map(t => dateKey(t.date))), [timesheets]);
  const todayFilled = timesheetDates.has(todayStr);

  const last7Days = useMemo(() => {
    const result: { date: Date; dateStr: string; filled: boolean; hours: number; statuses: string[] }[] = [];
    for (let i = 6; i >= 0; i--) {
      const d = subDays(today, i);
      const dateStr = format(d, 'yyyy-MM-dd');
      const entries = timesheets.filter(t => dateKey(t.date) === dateStr);
      result.push({
        date: d,
        dateStr,
        filled: entries.length > 0,
        hours: entries.reduce((sum, e) => sum + (e.hours || 0), 0),
        statuses: Array.from(new Set(entries.map(e => e.status))),
      });
    }
    return result;
  }, [timesheets]);

  /* ── Actions ──────────────────────────────────────────────── */

  const handleMarkToday = async () => {
    setMarking(true);
    setMarkError('');
    try {
      const now = new Date();
      const checkInTime = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}:00`;
      await attendanceService.markAttendance(checkInTime, 'Present');
      const monthStartStr = format(startOfMonth(today), 'yyyy-MM-dd');
      const data = await attendanceService.getMyAttendance(monthStartStr, todayStr);
      setAttendance(data || []);
    } catch (error: any) {
      setMarkError(error.response?.data?.message || 'Could not mark attendance.');
    } finally {
      setMarking(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h6" fontWeight={700} gutterBottom>
        Welcome back{user?.fullName ? `, ${user.fullName.split(' ')[0]}` : ''}!
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {format(today, 'EEEE, dd MMMM yyyy')}
      </Typography>

      <Grid container spacing={2.5}>
        {/* Leave balance */}
        <Grid item xs={12} sm={6} md={4}>
          <DashboardCard icon={<EventAvailable />} iconColor="#059669" title="Leave Balance">
            <Typography variant="h4" fontWeight={700}>{balance?.totalBalance ?? '—'}</Typography>
            <Typography variant="caption" color="text.secondary">days available (Sick + Earned share one balance)</Typography>
          </DashboardCard>
        </Grid>

        {/* Missed attendance */}
        <Grid item xs={12} sm={6} md={4}>
          <DashboardCard
            icon={<WarningAmber />}
            iconColor={pastMissedCount > 0 ? '#dc2626' : '#64748b'}
            title="Missed Attendance (this month)"
            onClick={() => setMissedDialogOpen(true)}
          >
            <Typography variant="h4" fontWeight={700}>{pastMissedCount}</Typography>
            <Typography variant="caption" color="text.secondary">day(s) missed</Typography>
            {todayPending && (
              <Chip label="Today not marked yet" size="small" color="warning" sx={{ mt: 1 }} />
            )}
          </DashboardCard>
        </Grid>

        {/* Upcoming holiday */}
        <Grid item xs={12} sm={6} md={4}>
          <DashboardCard icon={<Celebration />} iconColor="#7c3aed" title="Upcoming Holiday">
            {nextHoliday ? (
              <>
                <Typography variant="h6" fontWeight={700} noWrap>{nextHoliday.name}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {format(parseISO(dateKey(nextHoliday.date)), 'EEE, dd MMM yyyy')}
                </Typography>
                {upcomingHolidays.length > 1 && (
                  <Typography variant="caption" color="text.secondary">
                    +{upcomingHolidays.length - 1} more upcoming
                  </Typography>
                )}
              </>
            ) : (
              <Typography variant="body2" color="text.secondary">No upcoming holidays.</Typography>
            )}
          </DashboardCard>
        </Grid>

        {/* Upcoming leave */}
        <Grid item xs={12} sm={6} md={4}>
          <DashboardCard icon={<FlightTakeoff />} iconColor="#0284c7" title="Upcoming Leave">
            {nextLeave ? (
              <>
                <Typography variant="body1" fontWeight={600}>
                  {format(parseISO(dateKey(nextLeave.startDate)), 'dd MMM')} – {format(parseISO(dateKey(nextLeave.endDate)), 'dd MMM yyyy')}
                </Typography>
                <Typography variant="body2" color="text.secondary">{nextLeave.leaveType}</Typography>
                <Chip label={nextLeave.status} size="small" color={leaveStatusColor(nextLeave.status) as any} sx={{ mt: 1 }} />
              </>
            ) : (
              <>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>No upcoming leave planned.</Typography>
                {can('CreateLeaveRequest') && (
                  <Button size="small" variant="outlined" onClick={() => navigate('/dashboard/leave')}>
                    Apply for Leave
                  </Button>
                )}
              </>
            )}
          </DashboardCard>
        </Grid>

        {/* Goal sheet shortcut */}
        {can('ViewOwnGoals') && (
          <Grid item xs={12} sm={6} md={4}>
            <DashboardCard icon={<EmojiEvents />} iconColor="#d97706" title="Goal Sheet" onClick={() => navigate('/dashboard/goals')}>
              <Typography variant="body2" color="text.secondary">View and update your goals for this cycle.</Typography>
            </DashboardCard>
          </Grid>
        )}

        {/* Last 5 leaves applied */}
        <Grid item xs={12} sm={6} md={4}>
          <DashboardCard icon={<History />} iconColor="#4338ca" title="Recent Leave Applications" onClick={() => setLeavesDialogOpen(true)}>
            <Typography variant="h4" fontWeight={700}>{leaveRequests.length}</Typography>
            <Typography variant="caption" color="text.secondary">total applied — view last 5</Typography>
          </DashboardCard>
        </Grid>

        {/* Timesheet status */}
        <Grid item xs={12} sm={6} md={4}>
          <DashboardCard icon={<Assignment />} iconColor={todayFilled ? '#059669' : '#dc2626'} title="Today's Timesheet" onClick={() => setTimesheetDialogOpen(true)}>
            <Chip
              icon={todayFilled ? <CheckCircle /> : <Cancel />}
              label={todayFilled ? 'Filled' : 'Not Filled'}
              color={todayFilled ? 'success' : 'error'}
            />
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              View last 7 days
            </Typography>
          </DashboardCard>
        </Grid>
      </Grid>

      {/* Missed attendance dialog */}
      <Dialog open={missedDialogOpen} onClose={() => setMissedDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Missed Attendance — {format(today, 'MMMM yyyy')}</DialogTitle>
        <DialogContent dividers>
          {markError && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setMarkError('')}>{markError}</Alert>}
          {missedDays.length === 0 ? (
            <Typography color="text.secondary">No missed days this month. Great job!</Typography>
          ) : (
            <List disablePadding>
              {missedDays.map((d, idx) => (
                <React.Fragment key={d.dateStr}>
                  {idx > 0 && <Divider component="li" />}
                  <ListItem
                    secondaryAction={
                      d.isToday ? (
                        <Button size="small" variant="contained" disabled={marking} onClick={handleMarkToday}>
                          {marking ? 'Marking…' : 'Mark Attendance Now'}
                        </Button>
                      ) : (
                        <Button size="small" variant="outlined" onClick={() => { setMissedDialogOpen(false); navigate('/dashboard/attendance'); }}>
                          Regularize
                        </Button>
                      )
                    }
                  >
                    <ListItemIcon><WarningAmber color={d.isToday ? 'warning' : 'error'} /></ListItemIcon>
                    <ListItemText
                      primary={format(d.date, 'EEEE, dd MMM yyyy')}
                      secondary={d.isToday ? 'Not marked yet' : 'Missed'}
                    />
                  </ListItem>
                </React.Fragment>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMissedDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Last 5 leaves dialog */}
      <Dialog open={leavesDialogOpen} onClose={() => setLeavesDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Last 5 Leave Applications</DialogTitle>
        <DialogContent dividers>
          {last5Leaves.length === 0 ? (
            <Typography color="text.secondary">No leave requests yet.</Typography>
          ) : (
            <List disablePadding>
              {last5Leaves.map((l, idx) => (
                <React.Fragment key={l.id}>
                  {idx > 0 && <Divider component="li" />}
                  <ListItem>
                    <ListItemText
                      primary={
                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                          <span>{`${format(parseISO(dateKey(l.startDate)), 'dd MMM yyyy')} – ${format(parseISO(dateKey(l.endDate)), 'dd MMM yyyy')}  (${l.leaveType})`}</span>
                          {l.dayType === 'Half' && (
                            <Chip label="Half Day" size="small" variant="outlined" />
                          )}
                          {l.isUnpaid && l.leaveType !== 'Leave Without Pay' && (
                            <Chip label="Unpaid" color="warning" size="small" />
                          )}
                          {(() => {
                            const total = l.dayType === 'Half'
                              ? 0.5
                              : Math.round((parseISO(dateKey(l.endDate)).getTime() - parseISO(dateKey(l.startDate)).getTime()) / 86400000) + 1;
                            const paid = l.paidDays ?? total;
                            return !l.isUnpaid && paid < total ? (
                              <Chip label={`${paid} Paid + ${Math.round((total - paid) * 100) / 100} LWP`} color="warning" size="small" />
                            ) : null;
                          })()}
                        </Stack>
                      }
                      secondary={`Applied on ${format(parseISO(l.requestedAt), 'dd MMM yyyy')} — ${l.reason}`}
                    />
                    <Chip label={l.status} size="small" color={leaveStatusColor(l.status) as any} />
                  </ListItem>
                </React.Fragment>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setLeavesDialogOpen(false); navigate('/dashboard/leave'); }}>View All</Button>
          <Button onClick={() => setLeavesDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Last 7 days timesheet dialog */}
      <Dialog open={timesheetDialogOpen} onClose={() => setTimesheetDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Timesheet — Last 7 Days</DialogTitle>
        <DialogContent dividers>
          <List disablePadding>
            {last7Days.map((d, idx) => (
              <React.Fragment key={d.dateStr}>
                {idx > 0 && <Divider component="li" />}
                <ListItem>
                  <ListItemIcon>
                    {d.filled ? <CheckCircle color="success" /> : <Cancel color="error" />}
                  </ListItemIcon>
                  <ListItemText
                    primary={format(d.date, 'EEEE, dd MMM yyyy')}
                    secondary={d.filled ? `${d.hours}h logged — ${d.statuses.join(', ')}` : 'No entry logged'}
                  />
                </ListItem>
              </React.Fragment>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setTimesheetDialogOpen(false); navigate('/dashboard/entry'); }} startIcon={<PlayCircleFilled />}>
            Log Time
          </Button>
          <Button onClick={() => setTimesheetDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MyDashboard;
